import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Backpack,
  BarChart3,
  BookOpen,
  ChevronRight,
  ClipboardCheck,
  CloudSun,
  DownloadCloud,
  Fish,
  GraduationCap,
  Hammer,
  Heart,
  HeartHandshake,
  LifeBuoy,
  LogOut,
  Map,
  MapPinned,
  Medal,
  MessageCircle,
  Newspaper,
  Plus,
  Route,
  ScrollText,
  Settings,
  Share2,
  ShoppingCart,
  Sparkles,
  Tags,
  Target,
  Trash2,
  Trophy,
  UserRound,
  UsersRound,
  Utensils,
  Waves,
  CalendarDays,
} from "lucide-react";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import "../styles/layout/Sidebar.css";
import { useTranslation } from "react-i18next";
import SettingsPanel from "./SettingsPanel";
import useAndroidInstallPrompt from "../hooks/useAndroidInstallPrompt";
import API_BASE from "../api";
import { shareAppLink } from "../utils/shareApp";
import { APP_MODES } from "../utils/appMode";
import { THEME_MODES } from "../utils/themeMode";
import { isUkCountry } from "../utils/ukAccess";
import {
  showComingSoonSidebar,
  showGearDeals,
  showMarket,
  showMyStats,
  showRegulationsNav,
} from "../config/featureVisibility";

const ROOT_TAB_KEYS = new Set(["feed", "map", "groups", "market"]);

const BADGE_BASE_PATH = "/badges/";
const BADGE_EXTENSION = ".png";

export const SIDEBAR_SOCIAL_ITEMS = [
  {
    key: "facebook",
    labelKey: "sidebar_social_facebook",
    href: "https://www.facebook.com/profile.php?id=61589319998610",
    Icon: FaFacebookF,
  },
  {
    key: "instagram",
    labelKey: "sidebar_social_instagram",
    href: "https://www.instagram.com/reelitpro/",
    Icon: FaInstagram,
  },
  {
    key: "tiktok",
    labelKey: "sidebar_social_tiktok",
    href: "https://www.tiktok.com/@reelitpro",
    Icon: FaTiktok,
  },
  {
    key: "youtube",
    labelKey: "sidebar_social_youtube",
    href: "https://www.youtube.com/@prokopievseafishing4137",
    Icon: FaYoutube,
  },
];

function formatSidebarNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return "0";
  return new Intl.NumberFormat().format(parsed);
}

function parseSidebarDate(value) {
  if (!value) return null;

  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 10000000000 ? numeric * 1000 : numeric)
    : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatSidebarJoinedDate(value) {
  const date = parseSidebarDate(value);
  if (!date) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

function getBadgeSrc(badgeKey) {
  const normalizedKey = String(badgeKey || "").trim().toLowerCase();
  if (!normalizedKey) return "";

  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}${BADGE_BASE_PATH}${normalizedKey}${BADGE_EXTENSION}`;
}

function normalizeProfileAvatarPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function resolveProfileAvatarObjectPosition(targetUser) {
  const positionX = normalizeProfileAvatarPosition(targetUser?.avatarPositionX);
  const positionY = normalizeProfileAvatarPosition(targetUser?.avatarPositionY);
  return `${positionX}% ${positionY}%`;
}

function resolveProfileAvatarScale(targetUser) {
  const parsed = Number(targetUser?.avatarScale);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return parsed;
}

function appendAvatarCacheParam(src, updatedAt) {
  if (!src || !updatedAt) return src;
  return `${src}${src.includes("?") ? "&" : "?"}t=${encodeURIComponent(updatedAt)}`;
}

function resolveSidebarAvatarSrc(user) {
  const avatar = String(user?.avatar || "").trim();
  if (!avatar) return "";

  const src = /^https?:\/\//i.test(avatar) ? avatar : `${API_BASE}${avatar}`;
  return appendAvatarCacheParam(src, user?.updatedAt || user?.updated_at);
}

function getRootActiveKey(pathname, activeTab) {
  if (pathname !== "/") return "";
  return ROOT_TAB_KEYS.has(activeTab) ? activeTab : "feed";
}

export function getSidebarActiveKey({ pathname, activeTab, shellVariant }) {
  if (shellVariant === "profile") return "__my_profile";
  if (shellVariant === "user-profile") return "";

  const rootActiveKey = getRootActiveKey(pathname, activeTab);
  if (rootActiveKey) return rootActiveKey;

  if (pathname === "/profile") return "__my_profile";
  if (pathname === "/feed") return "feed";
  if (pathname === "/fishing-today") return "fishing_today";
  if (pathname === "/map" || pathname.startsWith("/map/")) return "map";
  if (pathname === "/groups" || pathname.startsWith("/groups/")) return "groups";
  if (
    pathname === "/trips" ||
    pathname.startsWith("/trips/") ||
    pathname === "/my-trips" ||
    pathname.startsWith("/my-trips/")
  ) {
    return "my_trips";
  }
  if (pathname === "/my-gear" || pathname.startsWith("/my-gear/")) return "/my-gear";
  if (pathname === "/favorite-spots" || pathname.startsWith("/favorite-spots/")) {
    return "/favorite-spots";
  }
  if (
    pathname === "/suggested-anglers" ||
    pathname.startsWith("/suggested-anglers/")
  ) {
    return "/suggested-anglers";
  }
  if (pathname === "/fishipedia/rankings") return "/fishipedia/rankings";
  if (pathname === "/fishipedia") return "/fishipedia";
  if (pathname === "/regulations" || pathname.startsWith("/regulations/")) {
    return "/regulations";
  }
  if (pathname === "/feedback" || pathname.startsWith("/feedback/")) return "/feedback";
  if (pathname === "/manual" || pathname.startsWith("/manual/")) return "/manual";
  if (pathname === "/surveys" || pathname.startsWith("/surveys/")) return "/surveys";
  if (pathname === "/gear-deals" || pathname.startsWith("/gear-deals/")) {
    return "/gear-deals";
  }
  if (pathname === "/admin/analytics") return "/admin/analytics";
  if (pathname === "/support" || pathname.startsWith("/support/")) return "/support";
  if (pathname === "/support-app" || pathname.startsWith("/support-app/")) {
    return "/support-app";
  }
  if (pathname === "/news" || pathname.startsWith("/news/")) return "/news";
  if (pathname === "/records" || pathname.startsWith("/records/")) return "/records";
  if (pathname === "/school" || pathname.startsWith("/school/")) return "/school";
  if (pathname === "/migration/link-supabase") return "/migration/link-supabase";

  return "";
}

export function buildMobileQuickLinkItems(t, canAccessFishingOutlook = false) {
  return [
    { key: "feed", iconComponent: Waves, label: t("feed") },
    ...(canAccessFishingOutlook === true
      ? [
          {
            key: "fishing_today",
            iconComponent: CloudSun,
            label: t("fishing_today_nav", { defaultValue: "Fishing Outlook" }),
            route: "/fishing-today",
          },
        ]
      : []),
    { key: "map", iconComponent: Map, label: t("map") },
    { key: "groups", iconComponent: UsersRound, label: t("groups") },
    {
      key: "my_trips",
      iconComponent: Route,
      label: t("my_trips"),
      route: "/trips",
    },
  ];
}

export default function Sidebar({
  setSidebarOpen,
  currentUser = null,
  renderInShell = false,
  shellVariant,
  isOpen = false,
  onClose,
  activeTab = "feed",
  onRootTabSelect,
  onOpenProfile,
  onSidebarRouteNavigate,
  onRequestDeleteAccount,
  onRequestLogout,
  onLogout,
  onOpenCreatePost,
  canViewAdminAnalytics = false,
  canAccessFishingOutlook = false,
  appMode = APP_MODES.FULL,
  isSimpleModeActive = false,
  onAppModeChange,
  themeMode = THEME_MODES.LIGHT,
  onThemeModeChange,
}) {
  const location = useLocation();
  const { t } = useTranslation();
  const install = useAndroidInstallPrompt();
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");
  const shareFeedbackTimeoutRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (shareFeedbackTimeoutRef.current) {
        window.clearTimeout(shareFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const handleShareAppClick = async () => {
    if (shareFeedbackTimeoutRef.current) {
      window.clearTimeout(shareFeedbackTimeoutRef.current);
      shareFeedbackTimeoutRef.current = null;
    }
    setShareFeedback("");

    try {
      const result = await shareAppLink({
        title: t("shareReelitPro", { defaultValue: "Share ReelitPro" }),
        text: t("shareReelitProText", {
          defaultValue: "Join me on ReelitPro and share your fishing adventures.",
        }),
      });

      if (result === "copied") {
        setShareFeedback(t("shareLinkCopied", { defaultValue: "Link copied" }));
        shareFeedbackTimeoutRef.current = window.setTimeout(() => {
          setShareFeedback("");
          shareFeedbackTimeoutRef.current = null;
        }, 2200);
      }
    } catch {
      setShareFeedback("");
    }
  };

  const isHomeShellRoute = location.pathname === "/";
  const resolvedShellVariant =
    shellVariant || (renderInShell && isHomeShellRoute ? "home" : undefined);
  const isModernShell = Boolean(resolvedShellVariant);
  const isModernMobileShell = isModernShell && isMobileViewport;
  const isModernDesktopShell = isModernShell && !isMobileViewport;
  const showMobileRootTabs = isModernMobileShell;
  const activePrimaryKey = getSidebarActiveKey({
    pathname: location.pathname,
    activeTab,
    shellVariant: resolvedShellVariant,
  });
  const closeSidebar = () => {
    onClose?.();
    setSidebarOpen?.(false);
  };
  const clearPendingNavigation = () => {
    // Kept as a single call site for handlers that used to debounce drawer navigation.
  };
  const handleRootTabSelect = (tabKey) => {
    if (!ROOT_TAB_KEYS.has(tabKey)) {
      return;
    }

    if (tabKey === "market" && !showMarket) {
      return;
    }

    if (isSimpleModeActive && tabKey === "market") {
      return;
    }

    clearPendingNavigation();
    closeSidebar();
    onRootTabSelect?.(tabKey);
  };
  const mobileQuickLinkItems = buildMobileQuickLinkItems(
    t,
    canAccessFishingOutlook
  );
  const canViewGearDeals = showGearDeals && isUkCountry(currentUser?.country);
  const simpleModeHiddenPaths = new Set([
    "/trips",
    "/my-gear",
    "/gear-deals",
    "/favorite-spots",
    "/suggested-anglers",
    "/fishipedia",
    "/fishipedia/rankings",
    "/regulations",
    "/records",
    "/news",
    "/school",
    "/feedback",
    "/manual",
    "/surveys",
    "/admin/analytics",
    "/migration/link-supabase",
  ]);
  const mobileDrawerSections = [
    {
      key: "my-account",
      title: t("mobile_sidebar_account_section", {
        defaultValue: "MY ACCOUNT",
      }),
      items: [
        {
          iconComponent: UserRound,
          label: "view_profile",
          path: "__my_profile",
          disabled: false,
        },
        ...(isModernMobileShell
          ? showMarket
            ? [
                {
                  iconComponent: ShoppingCart,
                  label: "market",
                  path: "market",
                  disabled: false,
                },
              ]
            : []
          : [
              {
                iconComponent: Route,
                label: "my_trips",
                path: "/trips",
                disabled: false,
              },
            ]),
        {
          iconComponent: Backpack,
          label: "my_gear",
          path: "/my-gear",
          disabled: false,
        },
        {
          iconComponent: Heart,
          label: "favorite_spots",
          path: "/favorite-spots",
          disabled: false,
        },
        ...(showMyStats
          ? [
              {
                iconComponent: BarChart3,
                label: "my_stats",
                path: "__my_stats",
                disabled: true,
                defaultLabel: "My Stats",
              },
            ]
          : []),
      ],
    },
    {
      key: "explore",
      title: t("mobile_sidebar_explore_section", {
        defaultValue: "EXPLORE",
      }),
      items: [
        { iconComponent: UsersRound, label: "sidebar_suggested_anglers", path: "/suggested-anglers", disabled: false, defaultLabel: "Suggested Anglers" },
        ...(canViewGearDeals
          ? [
              {
                iconComponent: Tags,
                label: "gear_deals_nav",
                path: "/gear-deals",
                disabled: false,
                defaultLabel: "Gear Deals",
              },
            ]
          : []),
        { iconComponent: Fish, label: "sidebar_fishipedia", path: "/fishipedia", disabled: false },
        { iconComponent: Trophy, label: "sidebar_user_rankings", path: "/fishipedia/rankings", disabled: false },
        // TODO: Re-enable Local Fishing Regulations when the regulations feature/content is completed for production.
        ...(showRegulationsNav
          ? [
              {
                iconComponent: ScrollText,
                label: "sidebar_regulations",
                path: "/regulations",
                disabled: false,
              },
            ]
          : []),
        { iconComponent: MessageCircle, label: "sidebar_feedback", path: "/feedback", disabled: false },
        { iconComponent: BookOpen, label: "sidebar_user_manual", path: "/manual", disabled: false },
        { iconComponent: ClipboardCheck, label: "sidebar_survey", path: "/surveys", disabled: false },
        ...(isModernMobileShell && install.visible
          ? [
              {
                iconComponent: DownloadCloud,
                label: "download_on_google_play",
                path: "__install_app",
                disabled: false,
                defaultLabel: "Download on Google Play",
              },
            ]
          : []),
      ],
    },
    ...(canViewAdminAnalytics
      ? [
          {
            key: "admin",
            title: t("mobile_sidebar_admin_section", {
              defaultValue: "ADMIN",
            }),
            items: [
              {
                iconComponent: BarChart3,
                label: "sidebar_admin_analytics",
                path: "/admin/analytics",
                disabled: false,
                defaultLabel: "Admin Analytics",
              },
            ],
          },
        ]
      : []),
    ...(showComingSoonSidebar
      ? [
          {
            key: "coming-soon",
            title: t("mobile_sidebar_coming_soon_section", {
              defaultValue: "COMING SOON",
            }),
            items: [
              { iconComponent: HeartHandshake, label: "sidebar_support_app", path: "/support-app", disabled: false },
              { iconComponent: Newspaper, label: "sidebar_news", path: "/news", disabled: false },
              { iconComponent: Medal, label: "sidebar_records", path: "/records", disabled: false },
              { iconComponent: Utensils, label: "sidebar_recipes", path: "/recipes", disabled: true },
              { iconComponent: GraduationCap, label: "sidebar_school", path: "/school", disabled: false },
              { iconComponent: Hammer, label: "sidebar_crafts", path: "/crafts", disabled: true },
              { iconComponent: Target, label: "sidebar_tournaments", path: "/tournaments", disabled: true },
            ],
          },
        ]
      : []),
    {
      key: "settings",
      title: t("mobile_sidebar_settings_section", {
        defaultValue: "SETTINGS",
      }),
      items: [
        {
          iconComponent: Settings,
          label: "settings",
          path: "__settings",
          disabled: false,
        },
        {
          iconComponent: LifeBuoy,
          label: "support",
          path: "/support",
          disabled: false,
          defaultLabel: "Support",
        },
        {
          iconComponent: Trash2,
          label: "delete_account",
          path: "__delete_account",
          disabled: false,
          defaultLabel: "Delete Account",
        },
        {
          iconComponent: LogOut,
          label: "logout",
          path: "__logout",
          disabled: false,
        },
      ],
    },
  ];
  const filterSimpleModeSection = (section) => {
    if (!isSimpleModeActive) {
      return section;
    }

    const items = section.items.filter((item) => {
      if (item.path === "market") return false;
      if (typeof item.path === "string" && simpleModeHiddenPaths.has(item.path)) {
        return false;
      }
      if (item.path === "__my_stats") return false;
      if (section.key === "coming-soon") return false;
      if (section.key === "admin") return false;
      return true;
    });

    return { ...section, items };
  };
  const filterSimpleModeDesktopSection = (section) => {
    if (!isSimpleModeActive) {
      return section;
    }

    const items = section.items.filter((item) => {
      if (section.key === "admin") return true;
      if (item.path === "market") return false;
      if (typeof item.path === "string" && simpleModeHiddenPaths.has(item.path)) {
        return false;
      }
      if (item.path === "__my_stats") return false;
      if (section.key === "coming-soon") return false;
      return true;
    });

    return { ...section, items };
  };
  const visibleMobileDrawerSections = mobileDrawerSections
    .map(filterSimpleModeSection)
    .filter((section) => section.items.length > 0);
  const visibleDesktopRootItems = mobileQuickLinkItems.filter(
    (item) =>
      !isSimpleModeActive ||
      !item.route ||
      !simpleModeHiddenPaths.has(item.route)
  );
  const visibleDesktopSidebarSections = mobileDrawerSections
    .map(filterSimpleModeDesktopSection)
    .map((section) => {
      if (section.key !== "my-account") {
        return section;
      }

      const items = section.items.flatMap((item) => {
        if (item.path !== "/trips") {
          return [item];
        }

        return showMarket
          ? [
              {
                iconComponent: ShoppingCart,
                label: "market",
                path: "market",
                disabled: false,
              },
            ]
          : [];
      });

      return { ...section, items };
    })
    .filter((section) => section.items.length > 0);

  function handleClick(item, e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (item.disabled) {
      alert(t("coming_soon"));
      return;
    }

    const path = item.path;

    if (path === "__my_profile") {
      clearPendingNavigation();
      closeSidebar();
      onOpenProfile?.();
      return;
    }

    if (path === "__logout") {
      clearPendingNavigation();
      closeSidebar();
      if (typeof onRequestLogout === "function") {
        onRequestLogout();
      } else {
        onLogout?.();
      }
      return;
    }

    if (path === "__delete_account") {
      clearPendingNavigation();
      closeSidebar();
      onRequestDeleteAccount?.();
      return;
    }

    if (path === "__settings") {
      clearPendingNavigation();
      setShowSettingsPanel(true);
      if (isModernMobileShell) {
        closeSidebar();
      }
      return;
    }

    if (typeof path === "string" && path.startsWith("/")) {
      if (typeof onSidebarRouteNavigate === "function") {
        clearPendingNavigation();
        onSidebarRouteNavigate(path);
        return;
      }

      clearPendingNavigation();
      closeSidebar();
      return;
    }

    if (item.rootTab) {
      handleRootTabSelect(item.rootTab);
      return;
    }

    if (ROOT_TAB_KEYS.has(path)) {
      handleRootTabSelect(path);
      return;
    }

    clearPendingNavigation();
    closeSidebar();
  }

  async function handleInstallClick(event) {
    event.preventDefault();
    event.stopPropagation();
    clearPendingNavigation();
    closeSidebar();
    await install.triggerPrompt();
  }

  function handleCreatePostClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (typeof onOpenCreatePost !== "function") {
      return;
    }

    clearPendingNavigation();
    closeSidebar();
    onOpenCreatePost(true);
  }

  function handleExploreMapClick(event) {
    event.preventDefault();
    event.stopPropagation();
    handleRootTabSelect("map");
  }

  if (isHomeShellRoute && !renderInShell) {
    return null;
  }

  const userAvatarSrc = resolveSidebarAvatarSrc(currentUser);
  const userName =
    currentUser?.displayName ||
    currentUser?.display_name ||
    currentUser?.name ||
    currentUser?.username ||
    t("app_title");
  const userInitial = (userName || "R").trim().charAt(0).toUpperCase() || "R";
  const progression = currentUser?.progression || {};
  const rankName =
    progression.currentRankName ||
    t("progression_rank_default", { defaultValue: "Newcomer" });
  const currentLevel = Math.max(
    1,
    Math.floor(Number(progression.currentLevel) || 1)
  );
  const formattedXp = formatSidebarNumber(progression.xp);
  const joinedDate = formatSidebarJoinedDate(currentUser?.createdAt);
  const badgeSrc = getBadgeSrc(progression.currentBadgeKey);
  const canCreatePost = typeof onOpenCreatePost === "function";
  const sidebarAvatarStyle = {
    objectPosition: resolveProfileAvatarObjectPosition(currentUser),
    transform: `scale(${resolveProfileAvatarScale(currentUser)})`,
  };

  const renderMenuIcon = (item, iconClassName) => {
    if (item.iconComponent) {
      const IconComponent = item.iconComponent;
      return <IconComponent className={iconClassName} aria-hidden="true" />;
    }

    if (item.iconSrc) {
      return (
        <img
          src={item.iconSrc}
          alt=""
          className={iconClassName}
          aria-hidden="true"
        />
      );
    }

    return item.icon;
  };

  const getSidebarItemLabel = (item) => {
    return item.defaultLabel
      ? t(item.label, { defaultValue: item.defaultLabel })
      : t(item.label);
  };

  const getSidebarItemActiveKey = (item) => {
    if (item.route) return item.key;
    if (item.key) return item.key;
    if (item.path === "/trips") return "my_trips";
    return item.path;
  };

  const isSidebarItemActive = (item) =>
    item.path !== "__install_app" &&
    item.path !== "__logout" &&
    !item.disabled &&
    activePrimaryKey === getSidebarItemActiveKey(item);

  const shouldShowItemChevron = (item) =>
    item.path !== "__logout" &&
    !item.disabled;

  const renderSidebarItemButton = (item, sectionKey, index) => {
    const isInstallItem = item.path === "__install_app";
    const isActive = isSidebarItemActive(item);
    const label = getSidebarItemLabel(item);

    return (
      <button
        key={`${sectionKey}-${item.label}-${index}`}
        type="button"
        className={`rp-sidebar__item${
          isActive ? " rp-sidebar__item--active" : ""
        }${item.disabled ? " disabled" : ""}`}
        aria-current={isActive ? "page" : undefined}
        onClick={
          isInstallItem
            ? handleInstallClick
            : (event) => handleClick(item, event)
        }
      >
        <span className="rp-sidebar__item-icon" aria-hidden="true">
          {renderMenuIcon(item, "sidebar-icon-svg")}
        </span>
        <span
          className={`rp-sidebar__item-copy${
            item.statusLabel ? " app-mode-switch__copy" : ""
          }`}
        >
          <span>{label}</span>
          {item.statusLabel ? (
            <span className="app-mode-switch__status">
              {item.statusLabel}
            </span>
          ) : null}
        </span>
        {shouldShowItemChevron(item) ? (
          <ChevronRight className="rp-sidebar__item-chevron" aria-hidden="true" />
        ) : null}
      </button>
    );
  };

  const settingsPanel = (
    <SettingsPanel
      isOpen={showSettingsPanel}
      onClose={() => setShowSettingsPanel(false)}
      appMode={appMode}
      onAppModeChange={onAppModeChange}
      themeMode={themeMode}
      onThemeModeChange={onThemeModeChange}
      currentUser={currentUser}
    />
  );

  if (isModernDesktopShell) {
    return (
      <>
      <aside className="rp-deskside" aria-label={t("app_title")}>
        <div className="rp-deskside__xp">
          <div className="rp-deskside__profile">
            <div className="rp-deskside__avatar-wrap">
              {userAvatarSrc ? (
                <img
                  src={userAvatarSrc}
                  alt={t("profile_picture")}
                  className="rp-deskside__avatar"
                  style={sidebarAvatarStyle}
                />
              ) : (
                <div className="rp-deskside__avatar">{userInitial}</div>
              )}
            </div>

            <div className="rp-deskside__profile-copy">
              <div className="rp-deskside__name">{userName}</div>
              <div
                className="rp-deskside__rank-badge"
                aria-label={t("progression_badge_popup_pill", {
                  defaultValue: "Level {{level}} - {{rank}}",
                  level: currentLevel,
                  rank: rankName,
                })}
              >
                {badgeSrc ? (
                  <img src={badgeSrc} alt="" className="rp-deskside__rank-icon" />
                ) : (
                  <Trophy className="rp-deskside__rank-icon" aria-hidden="true" />
                )}
                <span>{rankName}</span>
              </div>
            </div>
          </div>

          <div className="rp-deskside__stats" aria-label={t("profile_progression_title", { defaultValue: "Profile progression" })}>
            <div className="rp-deskside__stat">
              <Sparkles aria-hidden="true" />
              <span>{t("progression_xp_label", { defaultValue: "XP" })}</span>
              <strong>{formattedXp}</strong>
            </div>
            <div className="rp-deskside__stat">
              <CalendarDays aria-hidden="true" />
              <span>{t("profile_progression_joined_label")}</span>
              <strong>{joinedDate || t("common_empty")}</strong>
            </div>
          </div>
        </div>

          <div className="rp-deskside__body">
            <div className="rp-deskside__section">
              <div className="rp-deskside__section-label">
                {t("mobile_sidebar_quick_links_section")}
              </div>
              {visibleDesktopRootItems.map((item) => {
                const isActive = activePrimaryKey === getSidebarItemActiveKey(item);
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`rp-deskside__item${
                    isActive ? " rp-deskside__item--active" : ""
                  }`}
                  aria-current={isActive ? "page" : undefined}
                  onClick={(event) =>
                    item.route
                      ? handleClick(
                          {
                            ...item,
                            path: item.route,
                            disabled: false,
                          },
                          event
                        )
                      : handleRootTabSelect(item.key)
                  }
                >
                  <span className="rp-deskside__item-icon" aria-hidden="true">
                    {renderMenuIcon(item, "sidebar-icon-svg")}
                  </span>
                  <span className="rp-deskside__item-copy">
                    <span>{item.label}</span>
                  </span>
                  <ChevronRight className="rp-deskside__item-chevron" aria-hidden="true" />
                </button>
                );
              })}
            </div>

            {visibleDesktopSidebarSections.map((section) => (
              <div key={section.key} className="rp-deskside__section">
                {section.title ? (
                  <div className="rp-deskside__section-label">{section.title}</div>
                ) : null}

                {section.items.map((item, index) => {
                  const isInstallItem = item.path === "__install_app";
                  const isActive = isSidebarItemActive(item);
                  const label = getSidebarItemLabel(item);

                  return (
                    <button
                      key={`${section.key}-${item.label}-${index}`}
                      type="button"
                      className={`rp-deskside__item${
                        isActive ? " rp-deskside__item--active" : ""
                      }${item.disabled ? " disabled" : ""}`}
                      onClick={
                        isInstallItem
                          ? handleInstallClick
                          : (event) => handleClick(item, event)
                      }
                      >
                      <span className="rp-deskside__item-icon" aria-hidden="true">
                        {renderMenuIcon(item, "sidebar-icon-svg")}
                      </span>
                      <span
                        className={`rp-deskside__item-copy${
                          item.statusLabel ? " app-mode-switch__copy" : ""
                        }`}
                      >
                        <span>{label}</span>
                        {item.statusLabel ? (
                          <span className="app-mode-switch__status">
                            {item.statusLabel}
                          </span>
                        ) : null}
                      </span>
                      {shouldShowItemChevron(item) ? (
                        <ChevronRight className="rp-deskside__item-chevron" aria-hidden="true" />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>
        {settingsPanel}
      </>
      );
  }

  if (isModernMobileShell) {
    return (
      <>
        <button
          type="button"
          className={`rp-sidebar-backdrop${
            isOpen ? " rp-sidebar-backdrop--open" : ""
          }`}
          aria-label={t("common_close")}
          onClick={closeSidebar}
        />

        <aside
          className={`feed-sidebar rp-sidebar${
            isOpen ? " rp-sidebar--open" : ""
          }`}
          aria-hidden={!isOpen}
        >
          <div className="rp-sidebar__header">
            <button
              type="button"
              className="rp-sidebar__close-btn"
              aria-label={t("common_close")}
              onClick={closeSidebar}
            >
              {"\u00D7"}
            </button>

            <div className="rp-sidebar__user">
              <div className="rp-sidebar__avatar-wrap">
                {userAvatarSrc ? (
                  <img
                    src={userAvatarSrc}
                    alt={t("profile_picture")}
                    className="rp-sidebar__avatar"
                    style={sidebarAvatarStyle}
                  />
                ) : (
                  <div className="rp-sidebar__avatar">{userInitial}</div>
                )}
              </div>

              <div className="rp-sidebar__user-copy">
                <div className="rp-sidebar__user-name">{userName}</div>
                <div
                  className="rp-sidebar__rank-badge"
                  aria-label={t("progression_badge_popup_pill", {
                    defaultValue: "Level {{level}} - {{rank}}",
                    level: currentLevel,
                    rank: rankName,
                  })}
                >
                  {badgeSrc ? (
                    <img src={badgeSrc} alt="" className="rp-sidebar__rank-icon" />
                  ) : (
                    <Trophy className="rp-sidebar__rank-icon" aria-hidden="true" />
                  )}
                  <span>{rankName}</span>
                </div>
              </div>
            </div>

            <div className="rp-sidebar__stats" aria-label={t("profile_progression_title", { defaultValue: "Profile progression" })}>
              <div className="rp-sidebar__stat">
                <Sparkles aria-hidden="true" />
                <span>{t("progression_xp_label", { defaultValue: "XP" })}</span>
                <strong>{formattedXp}</strong>
              </div>
              <div className="rp-sidebar__stat">
                <CalendarDays aria-hidden="true" />
                <span>{t("profile_progression_joined_label")}</span>
                <strong>{joinedDate || t("common_empty")}</strong>
              </div>
            </div>
          </div>

          <div className="rp-sidebar__body">
            {canCreatePost ? (
              <button
                type="button"
                className="rp-sidebar__create-card"
                onClick={handleCreatePostClick}
              >
                <span className="rp-sidebar__create-icon" aria-hidden="true">
                  <Plus />
                </span>
                <span className="rp-sidebar__create-copy">
                  <span>{t("groups_create_post", { defaultValue: "Create Post" })}</span>
                  <small>
                    {t("create_post_sidebar_subtitle", {
                      defaultValue: "Share a catch, trip, or update.",
                    })}
                  </small>
                </span>
                <ChevronRight className="rp-sidebar__create-chevron" aria-hidden="true" />
              </button>
            ) : null}

            {showMobileRootTabs ? (
              <div className="rp-sidebar__section">
                <div className="rp-sidebar__section-label">
                  {t("mobile_sidebar_quick_links_section", {
                    defaultValue: "QUICK LINKS",
                  })}
                </div>
                <div className="rp-sidebar__section-items">
                  {mobileQuickLinkItems.map((item) => {
                    const isActive = activePrimaryKey === getSidebarItemActiveKey(item);
                    const handleQuickLinkClick = () => {
                      if (item.route) {
                        if (typeof onSidebarRouteNavigate === "function") {
                          clearPendingNavigation();
                          onSidebarRouteNavigate(item.route);
                          return;
                        }

                        clearPendingNavigation();
                        closeSidebar();
                        return;
                      }

                      handleRootTabSelect(item.key);
                    };

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`rp-sidebar__item${
                          isActive ? " rp-sidebar__item--active" : ""
                        }`}
                        aria-current={isActive ? "page" : undefined}
                        onClick={handleQuickLinkClick}
                      >
                        <span className="rp-sidebar__item-icon" aria-hidden="true">
                          {renderMenuIcon(item, "sidebar-icon-svg")}
                        </span>
                        <span className="rp-sidebar__item-copy">
                          <span>{item.label}</span>
                        </span>
                        <ChevronRight className="rp-sidebar__item-chevron" aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {visibleMobileDrawerSections.map((section) => (
              <div key={section.key} className="rp-sidebar__section">
                {section.title ? (
                  <div className="rp-sidebar__section-label">{section.title}</div>
                ) : null}

                <div className="rp-sidebar__section-items">
                  {section.items.map((item, index) =>
                    renderSidebarItemButton(item, section.key, index)
                  )}
                </div>
              </div>
            ))}

            <button
              type="button"
              className="rp-sidebar__map-card"
              onClick={handleExploreMapClick}
            >
              <span className="rp-sidebar__map-icon" aria-hidden="true">
                <MapPinned />
              </span>
              <span className="rp-sidebar__map-copy">
                <strong>
                  {t("sidebar_spots_cta_title", {
                    defaultValue: "Explore New Spots",
                  })}
                </strong>
                <span>
                  {t("sidebar_spots_cta_body", {
                    defaultValue: "Discover fishing spots on the map.",
                  })}
                </span>
              </span>
              <ChevronRight className="rp-sidebar__map-chevron" aria-hidden="true" />
            </button>

            <section
              className="rp-sidebar__social-card"
              aria-labelledby="rp-sidebar-social-title"
            >
              <h2 id="rp-sidebar-social-title">{t("sidebar_follow_reelitpro")}</h2>
              <div className="rp-sidebar__social-icons">
                {SIDEBAR_SOCIAL_ITEMS.map(({ key, labelKey, href, Icon }) => (
                  <a
                    className="rp-sidebar__social-button"
                    href={href}
                    key={key}
                    aria-label={t(labelKey)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon aria-hidden="true" />
                  </a>
                ))}
              </div>
            </section>
            <button
              type="button"
              className="rp-sidebar__share-app-button"
              onClick={handleShareAppClick}
              aria-label={t("shareReelitPro", { defaultValue: "Share ReelitPro" })}
            >
              <Share2 aria-hidden="true" />
              <span>{t("shareApp", { defaultValue: "Share app" })}</span>
            </button>
            <span className="rp-sidebar__share-app-status" aria-live="polite">
              {shareFeedback}
            </span>
          </div>
        </aside>
        {settingsPanel}
      </>
    );
  }

  return null;
}
